import React, { useState } from "react";
import { Button, Heading, TextField, toast } from "@telia/teddy";
import Autosuggest, { ChangeEvent } from "react-autosuggest";
import { useDispatch } from "react-redux";
import { API_URL_FIND_DELIVERY_ADDRESS_GEONORGE } from "../../../../../constants/api/checkout";
import { DeliveryAddressSuggestion } from "../../../../../models";
import { updateRetailerDeliveryAddress } from "../../state/actions";

const formatAddress = (address: DeliveryAddressSuggestion): string => {
    let result = address.streetName || "";
    result += address.streetNumber ? " " + address.streetNumber : "";
    result += address.streetNumberSuffix ? " " + address.streetNumberSuffix : "";
    result += address.postcode ? ", " + address.postcode : "";
    result += address.city ? " " + address.city : "";
    return result;
};

interface DeliveryAddressEditorProps {
    onToggleEdit: () => void;
    showEditForm: boolean;
}

const DeliveryAddressEditor: React.FC<DeliveryAddressEditorProps> = ({ onToggleEdit, showEditForm }) => {
    const dispatch = useDispatch();
    const [addressInput, setAddressInput] = useState("");
    const [addressSuggestions, setAddressSuggestions] = useState<DeliveryAddressSuggestion[]>([]);
    const [selectedAddress, setSelectedAddress] = useState<DeliveryAddressSuggestion | undefined>();
    const [savedDeliveryAddress, setSavedDeliveryAddress] = useState<DeliveryAddressSuggestion | undefined>();
    const [editAddressError, setEditAddressError] = useState<string | null>(null);
    const [isSavingAddress, setIsSavingAddress] = useState(false);

    const onSuggestionsFetchRequested = async ({ value }: { value: string }) => {
        if (value.trim().length <= 4) {
            setAddressSuggestions([]);
            return;
        }
        try {
            const result = await fetch(API_URL_FIND_DELIVERY_ADDRESS_GEONORGE(encodeURIComponent(value)), {
                credentials: "same-origin",
            });
            const data = await result.json();
            setAddressSuggestions(data.suggestedAddresses || []);
        } catch {
            setAddressSuggestions([]);
        }
    };

    const handleSaveDeliveryAddress = async () => {
        if (!selectedAddress) return;
        setEditAddressError(null);
        setIsSavingAddress(true);
        try {
            await dispatch(updateRetailerDeliveryAddress(selectedAddress));
            setSavedDeliveryAddress(selectedAddress);
            onToggleEdit();
            setAddressInput("");
            setSelectedAddress(undefined);
            toast.success("Leveringsadresse oppdatert", { position: "top-center" });
        } catch {
            setEditAddressError("Kunne ikke oppdatere leveringsadresse. Prøv igjen.");
        } finally {
            setIsSavingAddress(false);
        }
    };

    const handleCancel = () => {
        onToggleEdit();
        setEditAddressError(null);
        setSelectedAddress(undefined);
        setAddressInput("");
    };

    return (
        <div className="retailer-summary-edit-address">
            {showEditForm && (
                <div className="retailer-summary-edit-address__form">
                    <Autosuggest
                        suggestions={addressSuggestions}
                        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                        onSuggestionsClearRequested={() => setAddressSuggestions([])}
                        getSuggestionValue={formatAddress}
                        renderSuggestion={(suggestion) => (
                            <div className="retailer-summary-edit-address__suggestion">
                                <span>{formatAddress(suggestion)}</span>
                            </div>
                        )}
                        onSuggestionSelected={(_event, { suggestion }) => {
                            setSelectedAddress(suggestion);
                        }}
                        renderInputComponent={({ ref, ...inputProps }) => (
                            <TextField className="retailer-summary-edit-address__input-field">
                                <TextField.Input
                                    ref={ref as any}
                                    className="retailer-summary-edit-address__input"
                                    placeholder="Angi leveringsadresse her"
                                    data-di-mask
                                    {...(inputProps as any)}
                                />
                            </TextField>
                        )}
                        inputProps={{
                            value: addressInput,
                            onChange: (_event: React.FormEvent, { newValue }: ChangeEvent) => {
                                setAddressInput(newValue);
                                setSelectedAddress(undefined);
                            },
                            id: "retailer-delivery-address",
                        }}
                    />
                    {editAddressError && <p className="retailer-summary-edit-address__error">{editAddressError}</p>}
                    <div className="retailer-summary-edit-address__form-actions">
                        <Button
                            className="retailer-summary-edit-address__save"
                            onClick={handleSaveDeliveryAddress}
                            variant="secondary"
                            size="sm"
                            loading={isSavingAddress}
                            disabled={!selectedAddress || isSavingAddress}
                        >
                            Oppdater leveringsadresse
                        </Button>
                        <Button
                            className="retailer-summary-edit-address__cancel"
                            onClick={handleCancel}
                            variant="text"
                            size="sm"
                            disabled={isSavingAddress}
                        >
                            Avbryt
                        </Button>
                    </div>
                </div>
            )}
            {savedDeliveryAddress && !showEditForm && (
                <div className="retailer-summary-edit-address__saved">
                    <Heading as="h3" variant="title-100" className="retailer-summary-edit-address__heading">
                        Leveringsadresse:
                    </Heading>
                    {formatAddress(savedDeliveryAddress)}
                </div>
            )}
        </div>
    );
};

export default DeliveryAddressEditor;
